import React, { useCallback } from 'react';
import WidgetItem from './WidgetItem';
import { useTranslation } from 'react-i18next';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import TimelineIcon from '@material-ui/icons/Timeline';
import { FontSizeOutlined } from '@ant-design/icons';
import InsertChartIcon from '@material-ui/icons/InsertChart';
import WidgetPreview from './WidgetPreview';
import { StateDTO } from '../../../models/GeneralDTO';
import { useSelector } from 'react-redux';
import TableChartIcon from '@material-ui/icons/TableChart';
import { isProjectCertified, isTemplateTypeCertified } from '../../../shared/utils';
import { ProjectDTO } from '../../../models/ProjectDTO';
import { TEMPLATE_TYPE_NAME } from '../../../globalVariable/typeProject';
import { ConstantKeysWidgets } from '@photocarbone/nooco-shared-api-def';
export interface Widget {
  key: ConstantKeysWidgets;
  id?: number;
  name: string;
  description: string;
  icon: React.ReactNode;
  text?: string;
  secondText?: string;
  img?: ConstantKeyImg;
  imgPosition?: 'center' | 'top' | 'bottom';
  imgPath?: string;
  data?: { sortedBy: string };
}

export enum ConstantKeyImg {
  ACV_METHOD = 'acv_method',
  DATA_INPUT_NONE_CERTIFIED = 'data_input_none_certified',
}

const widgetListData: (t, currentProject: ProjectDTO) => Widget[] = (
  t,
  currentProject
) => {
  let epcmPresentation = {
    key: ConstantKeysWidgets.TEXT,
    name: t('APP.PDF_BUIDLER.WIDGETS_LIST.EPCM', {
      defaultValue: 'Introduction E+C-',
    }),
    description: t('APP.PDF_BUIDLER.WIDGETS_LIST.EPCM.DESCRIPTION', {
      defaultValue: 'Spécificité de la méthodologie E+C-',
    }),
    text: t('APP.PDF_BUIDLER.WIDGETS_LIST.EPCM.CONTENT', {
      defaultValue: 'Texte présentation EPCM à définir',
    }),
    icon: FontSizeOutlined,
  };

  let re2020Presentation = {
    key: ConstantKeysWidgets.TEXT,
    name: t('APP.PDF_BUIDLER.WIDGETS_LIST.RE2020', {
      defaultValue: 'Introduction RE2020',
    }),
    description: t('APP.PDF_BUIDLER.WIDGETS_LIST.RE2020.DESCRIPTION', {
      defaultValue: 'Spécificité de la méthodologie RE2020',
    }),
    text: t('APP.PDF_BUIDLER.WIDGETS_LIST.RE2020.CONTENT', {
      defaultValue: 'Texte présentation RE2020 à définir',
    }),
    icon: FontSizeOutlined,
  };

  // let scope3table = {
  //   key: ConstantKeysWidgets.TABLE_SCOPE3,
  //   name: t('APP.PDF_BUILDER.WIDGETS_LIST.SCOPE3_TABLE', {
  //     defaultValue: 'Scope 3 Table title',
  //   }),
  //   description: t('APP.PDF_BUILDER.WIDGETS_LIST.SCOPE3_TABLE.DESCRIPTION', {
  //     defaultValue: 'Scope 3 Table description',
  //   }),
  //   text: t('APP.PDF_BUILDER.WIDGETS_LIST.SCOPE3_TABLE.CONTENT', {
  //     defaultValue: 'Scope 3 Table content',
  //   }),
  //   icon: TableChartIcon,
  // };

  let widgetsList: Widget[] = [
    {
      key: ConstantKeysWidgets.HISTOGRAM,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.DESCRIPTION.TITLE', {
        defaultValue: "Leviers d'optimisation",
      }),
      description: t('APP.PDF_BUIDLER.WIDGETS_LIST.DESCRIPTION.HISTOGRAMME', {
        defaultValue: 'Principaux contributeurs à l’impact du projet',
      }),
      icon: InsertChartIcon,
      data: { sortedBy: 'potentiel' },
    },
    {
      key: ConstantKeysWidgets.HISTOGRAM,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.TITLE.IMPACT', {
        defaultValue: 'Tri par impact',
      }),
      description: t('APP.PDF_BUIDLER.WIDGETS_LIST.DESCRIPTION.HISTOGRAMME', {
        defaultValue: 'Principaux contributeurs à l’impact du projet',
      }),
      icon: InsertChartIcon,
    },
    {
      key: ConstantKeysWidgets.ACV,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.ACV_GRAPH.TITLE', {
        defaultValue: 'ACV GRAPH TITLE',
      }),
      description: t('APP.PDF_BUIDLER.WIDGETS_LIST.ACV_GRAPH.DESCRIPTION', {
        defaultValue: 'ACV GRAPH DESCRIPTION',
      }),
      text: t('APP.PDF_BUIDLER.WIDGETS_LIST.ACV_GRAPH.CONTENT', {
        defaultValue: 'ACV GRAPH CONTENT',
      }),
      icon: InsertChartIcon,
    },
    {
      key: ConstantKeysWidgets.TABLE,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.PROJECT_PRESENTATION.TITLE', {
        defaultValue: 'Présentation projet',
      }),
      description: t(
        'APP.PDF_BUIDLER.WIDGETS_LIST.PROJECT_PRESENTATION.DESCRIPTION',
        {
          defaultValue: 'Présentation des impacts carbone du projet',
        }
      ),
      icon: TableChartIcon,
    },
    {
      key: ConstantKeysWidgets.TEXTAREA,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.TEXTAREA.TITLE', {
        defaultValue: 'Texte libre',
      }),
      description: t('APP.PDF_BUIDLER.WIDGETS_LIST.TEXTAREA.DESCRIPTION', {
        defaultValue: 'Vignette de saisie libre de texte',
      }),
      icon: FontSizeOutlined,
    },
    {
      key: ConstantKeysWidgets.TEXT,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.GLOSSARY', {
        defaultValue: 'Glossaire',
      }),
      description: t('APP.PDF_BUIDLER.WIDGETS_LIST.GLOSSARY.DESCRIPTION', {
        defaultValue: 'Explication des acronymes et termes couramment utilisés',
      }),
      text: t('APP.PDF_BUIDLER.WIDGETS_LIST.GLOSSARY.CONTENT', {
        defaultValue: 'Texte glossaire à définir',
      }),
      icon: FontSizeOutlined,
    },
    {
      key: ConstantKeysWidgets.TEXT,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.ACV_METHOD', {
        defaultValue: 'Méthode ACV',
      }),
      description: t('APP.PDF_BUIDLER.WIDGETS_LIST.ACV_METHOD.DESCRIPTION', {
        defaultValue:
          'Explication des périmètres temporels et physiques couverts par la méthode ACV',
      }),
      text: t('APP.PDF_BUIDLER.WIDGETS_LIST.ACV_METHOD.CONTENT', {
        defaultValue: 'Texte méthode ACV à définir',
      }),
      secondText: t('APP.PDF_BUIDLER.WIDGETS_LIST.ACV_METHOD.CONTENT', {
        defaultValue: 'Texte numéro 2 en dessous méthode ACV à définir',
      }),
      icon: FontSizeOutlined,
    },
    {
      key: ConstantKeysWidgets.TEXT,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.DATA_INPUT', {
        defaultValue: 'Données d’entrée',
      }),
      description: t('APP.PDF_BUIDLER.WIDGETS_LIST.DATA_INPUT.DESCRIPTION', {
        defaultValue: 'Explication des différents types de données et leur sources',
      }),
      text: isTemplateTypeCertified(currentProject.templateType)
        ? t('APP.PDF_BUIDLER.WIDGETS_LIST.DATA_INPUT_NONE_CERTIFIED.CONTENT', {
            defaultValue: "Texte donnée d'entrée à définir",
          })
        : t('APP.PDF_BUIDLER.WIDGETS_LIST.DATA_INPUT_CERTIFIED.CONTENT', {
            defaultValue: "Texte donnée d'entrée à définir",
          }),
      icon: FontSizeOutlined,
    },
    {
      key: ConstantKeysWidgets.TEXT,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.NOOCO', {
        defaultValue: 'Présentation Nooco',
      }),
      description: t('APP.PDF_BUIDLER.WIDGETS_LIST.NOOCO.DESCRIPTION', {
        defaultValue: 'Qui est Nooco ?',
      }),
      text: t('APP.PDF_BUIDLER.WIDGETS_LIST.NOOCO_NONE_CERTIFIED.CONTENT', {
        defaultValue: 'Texte présentation Nooco à définir',
      }),
      icon: FontSizeOutlined,
    },
    {
      key: ConstantKeysWidgets.TEXT,
      name: t('APP.PDF_BUIDLER.WIDGETS_LIST.CERTIFICATION_NOOCO', {
        defaultValue: 'Certifications Nooco',
      }),
      description: t(
        'APP.PDF_BUIDLER.WIDGETS_LIST.CERTIFICATION_NOOCO.DESCRIPTION',
        {
          defaultValue: 'Certifications Nooco description',
        }
      ),
      text: t('APP.PDF_BUIDLER.WIDGETS_LIST.CERTIFICATION_NOOCO.CONTENT', {
        defaultValue: 'Certifications Nooco CONTENT',
      }),
      icon: FontSizeOutlined,
    },
  ];

  if (currentProject.templateType === TEMPLATE_TYPE_NAME.EPCM) {
    widgetsList.push(epcmPresentation);
  } else if (currentProject.templateType === TEMPLATE_TYPE_NAME.RE2020) {
    widgetsList.push(re2020Presentation);
  }
  // else if (currentProject.templateType === TEMPLATE_TYPE_NAME.GLOBAL_MONITORING) {
  //   widgetsList.push(scope3table);
  // }

  return widgetsList;
};

function WidgetsList() {
  const { t } = useTranslation();
  const { currentProject } = useSelector((state: StateDTO) => state.projects);

  const WidgetsListData = widgetListData(t, currentProject);

  return (
    <>
      <WidgetPreview />
      <div className="border-section padding-section" data-testid="widgets-list">
        <div className="header-section">
          <p className="title-section">
            {t('APP.PDF_BUILDER.WIDGETS_LIST_TITLE', { defaultValue: 'Vignettes' })}
          </p>{' '}
          <p className="subtitle-section">
            {t('APP.PDF_BUILDER.DRAG_AND_DROP', {
              defaultValue: 'Glisser/Déposer les vignettes dans les sections',
            })}
          </p>
        </div>
        <div style={{ overflowY: 'scroll', height: '93%' }}>
          {WidgetsListData.map((widget) => (
            <WidgetItem widget={widget} key={widget.id} moveCard={() => {}} />
          ))}
        </div>
      </div>
    </>
  );
}

export default WidgetsList;
